// Not defteri widget'ı — metin `UserSettings["Notepad<id>"]` içinde saklanır (Blazor ile ortak).
//
// Düz metnin üstüne iki şey eklendi:
//   1. Yapılacaklar: `- [ ]` ile başlayan satırlar onay kutusu olarak çizilir ve tıklanır.
//      Ayrı bir veri yapısı yok — metin Blazor'da da okunabilir kalıyor.
//   2. Kaydetme durumu: kayıt sessizce yapılıyordu ve hata yutuluyordu; yazdığı not
//      gitmemiş olabilecek kullanıcının bunu görmesi gerekir.
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, ListTodo, RotateCw } from 'lucide-react';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { useAuthStore } from '@/entities/session/model/authStore';
import { Textarea } from '@/shared/ui/Textarea';
import { IconButton } from '@/shared/ui/IconButton';
import { Spinner } from '@/shared/ui/Spinner';
import { cn } from '@/shared/lib/cn';
import { continueList, noteProgress, parseNote, toggleNoteLine } from '../lib/checklist';

interface UserSettingDto { settingValue: string | null }

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function NotepadWidget({ widgetId }: { widgetId: string }) {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.userId ?? '');
  const key = widgetId === 'notepad' ? 'Notepad' : `Notepad_${widgetId}`;
  const [text, setText] = useState<string | null>(null);
  const [state, setState] = useState<SaveState>('idle');
  /** Liste kipinde kutular çizilir; düzenleme kipinde ham metin. */
  const [editing, setEditing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  const q = useQuery({
    queryKey: ['home', 'notepad', userId, key],
    queryFn: () => apiClient.get<UserSettingDto>(ENDPOINTS.USER_SETTING(userId, key), { octo: { expectSuccess: false } }),
    enabled: !!userId,
    staleTime: Infinity,
  });
  const loaded = q.isSuccess || q.isError;
  const hydrated = useRef(false);
  useEffect(() => {
    if (!loaded || hydrated.current) return;
    hydrated.current = true;
    setText(q.data?.settingValue ?? '');
  }, [loaded, q.data]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const save = (value: string) => {
    setState('saving');
    void apiClient
      .post(ENDPOINTS.USER_SETTINGS_SAVE, { userId, settingKey: key, settingValue: value }, { octo: { expectSuccess: false } })
      .then(() => setState('saved'))
      .catch(() => setState('error'));
  };

  const update = (value: string, immediate = false) => {
    setText(value);
    if (timer.current) clearTimeout(timer.current);
    if (immediate) save(value);
    else timer.current = setTimeout(() => save(value), 800);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const el = e.currentTarget;
    const next = continueList(el.value, el.selectionStart);
    if (!next) return;
    // Madde listesini elle sürdürmek zorunda kalmamak için Enter devralınıyor.
    e.preventDefault();
    update(next.text);
    requestAnimationFrame(() => el.setSelectionRange(next.caret, next.caret));
  };

  const value = text ?? '';
  const lines = parseNote(value);
  const progress = noteProgress(value);
  const hasTodos = progress.total > 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-1">
      <div className="flex items-center gap-1">
        {hasTodos ? (
          <span className="text-fg-subtle text-[11px] tabular-nums">
            {progress.done}/{progress.total}
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-1">
          {state === 'saving' ? <Spinner className="text-fg-subtle h-3 w-3" /> : null}
          {state === 'saved' ? <Check className="text-success h-3.5 w-3.5" aria-label={t('home.notepad.saved')} /> : null}
          {state === 'error' ? (
            <IconButton size="sm" label={t('home.notepad.retry')} onClick={() => save(value)} className="text-danger">
              <RotateCw className="h-3.5 w-3.5" />
            </IconButton>
          ) : null}
          <IconButton
            size="sm"
            label={editing ? t('home.notepad.showList') : t('home.notepad.edit')}
            aria-pressed={editing}
            onClick={() => setEditing((v) => !v)}
          >
            <ListTodo className={cn('h-3.5 w-3.5', !editing && hasTodos && 'text-brand-600')} />
          </IconButton>
        </span>
      </div>

      {editing || !hasTodos ? (
        <Textarea
          ref={areaRef}
          value={value}
          onChange={(e) => update(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => timer.current && update(value, true)}
          placeholder={t('home.notepadPlaceholder')}
          aria-label={t('home.widget.notepad')}
          className="min-h-0 w-full flex-1 resize-none"
        />
      ) : (
        /* Liste kipi: yalnızca kutular tıklanır, diğer satırlar metin olarak okunur.
           Düzenlemek için başlıktaki düğmeyle ham metne geçilir. */
        <ul className="min-h-0 flex-1 overflow-auto pr-1 text-sm">
          {lines.map((line) =>
            line.checked === null ? (
              line.text.trim() ? (
                <li key={line.index} className="text-fg-muted px-1 py-0.5 text-xs">
                  {line.text}
                </li>
              ) : null
            ) : (
              <li key={line.index}>
                <button
                  type="button"
                  onClick={() => update(toggleNoteLine(value, line.index), true)}
                  className="hover:bg-bg-hover focus-visible:ring-ring flex w-full items-start gap-2 rounded px-1 py-0.5 text-left focus-visible:ring-2 focus-visible:outline-none"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      line.checked ? 'bg-brand-600 border-brand-600 text-white' : 'border-border-strong',
                    )}
                  >
                    {line.checked ? <Check className="h-3 w-3" /> : null}
                  </span>
                  <span className={cn('min-w-0 flex-1', line.checked && 'text-fg-subtle line-through')}>{line.text}</span>
                </button>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}
